import React from 'react';
import styled from '@emotion/styled/macro';
import vars from '@variables';
import {
  EMFLogoAtom,
  CyberEssentialsLogoAtom,
} from '@atoms/logo-atoms/logo-atoms';
import { FooterBlockProps } from './footer-block.types';
import { GTM_ID } from '../../../../common/constants/gtmIds';
import {
  InternalLinkAtom,
  ContentfulLinkAtom,
} from '@atoms/link-atoms/link-atoms';
import { PAtom } from '@atoms/text-atoms/text-atoms';
import { StyledSocialLinks as SocialLinks } from '@components/social-links/social-links';

const FooterBlock: React.FC<FooterBlockProps> = ({
  footerData: {
    description,
    registrationNumbers,
    primaryLinks,
    secondaryLinks = [],
    socialLinks,
    partners,
    subContentLinks,
    translate = true,
    poweredByContentful,
  },
  className,
}) => {
  return (
    <div className={className} data-testid="FooterBlock" role="contentinfo">
      <div className="main-content">
        <div className="content-container">
          <div className="column-1">
            <EMFLogoAtom />
            <PAtom>{description}</PAtom>
            {registrationNumbers.map((registrationNumber, index) => (
              <PAtom
                className="registration-number"
                key={`${index}--${registrationNumber.slice(0, 2)}`}
              >
                {registrationNumber}
              </PAtom>
            ))}
          </div>
          <div className="column-2">
            {primaryLinks.map((primaryLink, index) => (
              <InternalLinkAtom
                gtmId={GTM_ID.MAIN_FOOTER_LINK}
                url={primaryLink.url}
                key={`${index}--${primaryLink.title.slice(0, 2)}`}
              >
                {primaryLink.title}
              </InternalLinkAtom>
            ))}
            <SocialLinks
              socialLinks={socialLinks}
              fill={vars.coreDarkGrey}
              translate={translate}
            />
          </div>
          <div className="column-3">
            {secondaryLinks.length > 0
              ? secondaryLinks.map((secondaryLink, index) => (
                  <InternalLinkAtom
                    gtmId={GTM_ID.MAIN_FOOTER_LINK}
                    url={secondaryLink.url}
                    key={`${index}--${secondaryLink.title.slice(0, 2)}`}
                  >
                    {secondaryLink.title}
                  </InternalLinkAtom>
                ))
              : null}
            <PAtom>
              {partners.text}
              <a href={partners.linkUrl}>{partners.linkText}</a>
            </PAtom>
            <SocialLinks
              socialLinks={socialLinks}
              fill={vars.coreDarkGrey}
              translate={translate}
            />
            <CyberEssentialsLogoAtom />
          </div>
        </div>
      </div>
      <div className="sub-content">
        <div className="content-container">
          <div className="copyright-container">
            &copy; Ellen MacArthur Foundation
          </div>
          <div className="links-container">
            <ContentfulLinkAtom
              url={poweredByContentful.url}
              text={poweredByContentful.text}
            />
            {subContentLinks.map((link, index) => (
              <InternalLinkAtom
                gtmId={GTM_ID.SUB_FOOTER_LINK}
                url={link.url}
                key={`${index}--${link.title.slice(0, 2)}`}
              >
                {link.title}
              </InternalLinkAtom>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const StyledFooterBlock = styled(FooterBlock)`
  @media screen and (min-width: ${vars.breakPointTablet}) {
    height: 43rem;
    background-color: ${vars.greyHavens};
    .main-content,
    .sub-content {
      border-top: 1px dashed ${vars.borderGrey};
      .content-container {
        display: flex;
        justify-content: space-between;
        max-width: ${vars.contentMaxWidth};
        margin: 0 auto;
      }
    }
  }
  .main-content {
    border: none;
    background-color: ${vars.greyHavens};
    .content-container {
      @media (min-width: ${vars.breakPointTablet}) and (max-width: ${vars.breakPointTwelveColumn}) {
        padding: calc(2 * ${vars.spacingL}) 0 calc(2 * ${vars.spacingL}) 4%;
      }
      @media (min-width: ${vars.breakPointTwelveColumn}) {
        padding: calc(2 * ${vars.spacingL}) ${vars.spacingL}
          calc(2 * ${vars.spacingL});
      }
      display: flex;
      align-items: center;
      .column-1 {
        flex-basis: 80%;
        @media (min-width: ${vars.breakPointTwelveColumn}) {
          flex-basis: 40%;
        }
        ${EMFLogoAtom} {
          width: 21rem;
          height: 5rem;
          margin-bottom: ${vars.spacingXl};
        }
        ${PAtom} {
          ${vars.fontSize3}
          padding-bottom: ${vars.spacingM};
          line-height: 1.4;
        }
        @media screen and (min-width: ${vars.breakPointTablet}) {
          .registration-number {
            padding: 0;
          }
        }
      }
      .column-2 {
        margin-left: 4rem;
        @media (min-width: ${vars.breakPointTablet}) and (max-width: ${vars.breakPointTwelveColumn}) {
          flex-basis: 45%;
        }
        @media screen and (min-width: ${vars.breakPointTwelveColumn}) {
          flex-basis: 20%;
        }
        ${InternalLinkAtom} {
          display: block;
          width: fit-content;
          padding: ${vars.spacingS} 0;
          color: ${vars.coreDarkGrey};
          text-decoration: none;
          margin-bottom: 0.6rem;
          ${vars.fontSize5}
          font-weight: ${vars.fontWeightBold};
        }
        ${SocialLinks} {
          padding-left: ${vars.spacingXs};
          display: none;
          @media (min-width: ${vars.breakPointTablet}) and (max-width: ${vars.breakPointTwelveColumn}) {
            display: block;
            ${PAtom} {
              padding-top: ${vars.spacingL};
              ${vars.fontSize4}
            }
          }
        }
      }
      .column-3 {
        margin-left: calc(3 * -${vars.spacingXl});
        flex-basis: 30%;
        @media screen and (min-width: ${vars.breakPointTwelveColumn}) {
          flex-basis: 20%;
        }
        ${InternalLinkAtom} {
          display: block;
          width: fit-content;
          padding: ${vars.spacingS} ${vars.spacingS} ${vars.spacingS} 0;
          ${vars.fontSize5}
          font-weight: ${vars.fontWeightBold};
          color: ${vars.coreDarkGrey};
          text-decoration: none;
          margin-bottom: 0.6rem;
        }
        ${PAtom} {
          padding-right: ${vars.spacingL};
          margin-top: 0;
          margin-bottom: 0;
          line-height: 1.4;
          font-size: 16px;
        }
        ${SocialLinks} {
          display: none;
          @media screen and (min-width: ${vars.breakPointTwelveColumn}) {
            display: block;
            padding-top: ${vars.spacingL};
          }
          padding: ${vars.spacingXs};
        }
      }
    }
  }
  .sub-content {
    background-color: ${vars.white};
    .content-container {
      justify-content: space-between;
      ${vars.fontSize2}
      padding: ${vars.spacingL};
      ${InternalLinkAtom} {
        color: ${vars.coreDarkGrey};
        margin-left: ${vars.spacingL};
      }
    }
  }
`;

export default StyledFooterBlock;
